import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Search, Truck, PackageX, Check } from "lucide-react";
import api from "@/lib/api";

const isAvailable = (p, pincode) => {
  if (p.is_global) return true;
  const cp = String(pincode || "").trim();
  if (!cp) return false;
  const list = (p.available_pincodes || []).map((x) => String(x).trim());
  if (list.length) return list.includes(cp);
  const legacy = String(p.delivery_pincode || "").trim();
  return !!legacy && legacy === cp;
};

const isDeliverable = (p, pincode) => {
  // "Free delivery" = the product carries no fee. We still gate visibility
  // separately in the ADD picker via isAvailable().
  return (p.delivery_fee_type || "free") === "free";
};

/** Picker used from Checkout for either "Add more products" or "Change" (replace). */
export default function ProductPickerModal({
  open,
  mode = "add",         // "add" | "change"
  pincode = "",
  restrictCategory,     // required in "change" mode
  excludeIds = [],      // hide these product ids (e.g., current line)
  onPick,               // (product) => void
  onClose,
}) {
  const [all, setAll] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(restrictCategory || "");
  const [q, setQ] = useState("");
  const [pickedId, setPickedId] = useState(null);

  // Load admin-managed categories once — dynamic, no hardcoded list.
  useEffect(() => {
    if (!open || restrictCategory) return;
    api.get("/categories").then((r) => {
      const list = r.data || [];
      setCategories(list);
      setCategory((prev) => (prev && list.some((c) => c.slug === prev) ? prev : (list[0]?.slug || "")));
    }).catch(() => setCategories([]));
  }, [open, restrictCategory]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPickedId(null);
    setQ("");
    // In change mode, only fetch same-category products; else fetch everything.
    const url = restrictCategory ? `/products?category=${restrictCategory}` : "/products";
    api.get(url)
      .then((r) => setAll(r.data || []))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
    if (restrictCategory) setCategory(restrictCategory);
  }, [open, restrictCategory]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const filtered = useMemo(() => {
    const pool = restrictCategory ? all : all.filter((p) => p.category === category);
    const query = q.trim().toLowerCase();
    return pool
      .filter((p) => p.available !== false)
      .filter((p) => !excludeSet.has(p.id))
      // Enforce pincode availability when we have one; in ADD mode always,
      // in CHANGE mode also so users can't swap to something out-of-area.
      .filter((p) => (pincode ? isAvailable(p, pincode) : true))
      .filter((p) =>
        !query
          ? true
          : (p.name || "").toLowerCase().includes(query) ||
            (p.description || "").toLowerCase().includes(query) ||
            (p.tags || []).some((t) => (t || "").toLowerCase().includes(query))
      )
      // Deliverable (free) first, then fee-carrying.
      .sort((a, b) => Number(isDeliverable(b, pincode)) - Number(isDeliverable(a, pincode)));
  }, [all, category, q, pincode, excludeSet, restrictCategory]);

  if (!open) return null;

  const title = mode === "change" ? "Change product" : "Add more products";
  const subtitle =
    mode === "change"
      ? `Pick a replacement in ${restrictCategory || "the same category"}.`
      : "Browse the full catalogue and add anything you'd like.";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-charcoal/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="product-picker-modal"
      >
        <motion.div
          initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 20 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        >
          <header className="p-5 sm:p-6 border-b border-warm flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-terracotta">
                {mode === "change" ? "Replace" : "Add"}
              </p>
              <p className="font-display text-lg sm:text-xl text-charcoal leading-tight">{title}</p>
              <p className="text-xs text-charcoal/60 mt-0.5">{subtitle}</p>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 hover:bg-linen-2"
              data-testid="product-picker-close"
            >
              <X className="w-5 h-5" />
            </button>
          </header>

          {/* Category tabs — only in add mode */}
          {mode === "add" && (
            <div className="px-5 sm:px-6 pt-4 flex flex-wrap gap-2 border-b border-warm pb-4" data-testid="picker-tabs">
              {categories.length === 0 ? (
                <p className="text-sm text-charcoal/50">No categories available.</p>
              ) : (
                categories.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => setCategory(t.slug)}
                    data-testid={`picker-tab-${t.slug}`}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest border transition-all ${
                      category === t.slug
                        ? "bg-charcoal text-white border-charcoal"
                        : "bg-white text-charcoal/70 border-warm hover:border-charcoal/40"
                    }`}
                  >
                    {t.name}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Search bar */}
          <div className="px-5 sm:px-6 pt-4 pb-3">
            <div className="relative">
              <Search className="w-4 h-4 text-charcoal/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={mode === "change"
                  ? "Search alternatives…"
                  : "Search flowers, cakes, decorations, hampers…"}
                className="w-full rounded-xl bg-white border border-warm pl-9 pr-4 py-2.5 focus:outline-none focus:border-terracotta text-sm"
                data-testid="picker-search"
              />
            </div>
            {pincode && (
              <p className="text-[11px] text-charcoal/50 mt-2">
                Showing products deliverable to <b>{pincode}</b> first.
              </p>
            )}
          </div>

          {/* Product grid */}
          <div className="p-5 sm:p-6 pt-2 overflow-y-auto flex-1">
            {loading ? (
              <div className="py-16 text-center text-charcoal/50">Loading products…</div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-charcoal/50" data-testid="picker-empty">
                No products match your search.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {filtered.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    picked={pickedId === p.id}
                    deliverable={isDeliverable(p, pincode)}
                    onSelect={() => setPickedId(p.id)}
                  />
                ))}
              </div>
            )}
          </div>

          <footer className="p-4 sm:p-5 border-t border-warm flex items-center justify-between bg-linen-2 gap-3">
            <p className="text-xs text-charcoal/60">
              {pickedId
                ? "Ready to " + (mode === "change" ? "replace" : "add")
                : "Pick a product to continue"}
            </p>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="pill-btn pill-btn-secondary text-sm"
                data-testid="picker-cancel"
              >
                Cancel
              </button>
              <button
                disabled={!pickedId}
                onClick={() => {
                  const prod = all.find((p) => p.id === pickedId);
                  if (prod) onPick(prod);
                }}
                className="rounded-full px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-terracotta/30 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none bg-terracotta"
                data-testid="picker-confirm"
              >
                {mode === "change" ? "Replace product" : "Add to cart"}
              </button>
            </div>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ProductCard({ product: p, picked, deliverable, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`picker-product-${p.id}`}
      className={`text-left rounded-2xl border-2 bg-white overflow-hidden transition-all ${
        picked
          ? "border-terracotta shadow-md ring-2 ring-terracotta/20"
          : "border-warm hover:border-charcoal/30"
      }`}
    >
      <div className="aspect-square bg-linen-2 relative">
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-charcoal/30 text-xs">
            No image
          </div>
        )}
        {picked && (
          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-terracotta text-white flex items-center justify-center shadow-lg">
            <Check className="w-4 h-4" strokeWidth={3} />
          </div>
        )}
        <div className="absolute bottom-2 left-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${
              deliverable
                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                : "bg-amber-100 text-amber-700 border-amber-200"
            }`}
            data-testid={`picker-tag-${p.id}`}
          >
            {deliverable ? <Truck className="w-3 h-3" /> : <PackageX className="w-3 h-3" />}
            {deliverable ? "Deliverable" : "Delivery fee applies"}
          </span>
        </div>
      </div>
      <div className="p-3">
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-charcoal/50">
          {p.category}
        </p>
        <p className="font-semibold text-charcoal text-sm mt-0.5 line-clamp-2">{p.name}</p>
        <p className="font-display text-lg text-charcoal mt-1">₹{p.price}</p>
      </div>
    </button>
  );
}
